import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  X, Zap, Bot, Brain, Car, Wrench, 
  BarChart3, FileText, Package, Users, Cat, Sparkles, ShoppingBasket, Plus, Mail, Globe,
  Home // Standardized branding icon
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';

export const PricingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const corePlans = [
    {
      id: 'free',
      name: t('pricing.free_name', 'Free'),
      tagline: t('pricing.free_tag', 'Discover Everyday'),
      monthlyPrice: '$0',
      annualPrice: '$0',
      icon: '🌱',
      featureIcon: '✅',
      features: [
        t('pricing.free.features.personal_use', "Personal Use Only"), 
        t('pricing.free.features.single_user', "Single User"), 
        t('pricing.free.features.standard_google_sync', "Standard Google Sync"), 
        t('pricing.free.features.private_task_list', "Private Task List"), 
        t('pricing.free.features.grocery_sandbox', "Grocery Sandbox"), 
        t('pricing.free.features.data_history_30_day', "30-Day Data History"), 
        t('pricing.free.features.limited_automations', "Limited Automations")
      ],
      notIncluded: [
        t('pricing.free.not_included.shared_households', "Shared Households"), 
        t('pricing.free.not_included.more_than_1_user', "More than 1 User"), 
        t('pricing.free.not_included.collaborative_calendar', "Collaborative Calendar"), 
        t('pricing.free.not_included.shared_task_boards', "Shared Task Boards"), 
        t('pricing.free.not_included.maintenance_asset_registries', "Maintenance & Asset Registries"), 
        t('pricing.free.not_included.longer_data_history', "Longer Data History"), 
        t('pricing.free.not_included.smart_automations', "Smart Automations")
      ],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-slate-200 dark:border-slate-800'
    },
    {
      id: 'basic',
      name: t('pricing.basic_name', 'Basic'),
      tagline: t('pricing.basic_tag', 'Free Plan +'),
      monthlyPrice: '$9.99',
      annualPrice: '$89.99', // $119.88 annually, discounted to $89.99
      annualOriginalPrice: '$119.99',
      icon: '🌿',
      featureIcon: '⭐',
      recommended: true,
      features: [
        t('pricing.basic.features.one_shared_household', "1 Shared Household"), 
        t('pricing.basic.features.up_to_6_members', "Up to 6 Members / Household"), 
        t('pricing.basic.features.collaborative_shared_hub', "Collaborative Shared Hub (Calendar)"), 
        t('pricing.basic.features.shared_kanban_boards', "Shared Kanban Boards (Tasks)"), 
        t('pricing.basic.features.maintenance_contacts_registry', "Maintenance & Contacts Registry"), 
        t('pricing.basic.features.data_history_5_year', "5-Year Data History"), 
        t('pricing.basic.features.basic_recurring_logic', "Basic Recurring Logic (Automations)"),
        t('pricing.basic.features.roadmap_votes_suggestions', "Roadmap Votes & Suggestions"),
        t('pricing.basic.features.support', "Support")
      ],
      notIncluded: [
        t('pricing.basic.not_included.multiple_shared_households', "Multiple Shared Households"), 
        t('pricing.basic.not_included.ai_generated_tasks', "AI-Generated Tasks"), 
        t('pricing.basic.not_included.asset_vault', "Asset Vault (Registries)"), 
        t('pricing.basic.not_included.unlimited_data_history', "Unlimited Data History"), 
        t('pricing.basic.not_included.commute_habit_iq', "Commute & Habit IQ (Automations)")
      ],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-brand ring-4 ring-blue-600/10'
    },
    {
      id: 'premium',
      name: t('pricing.premium_name', 'Premium'),
      tagline: t('pricing.premium_tag', 'Basic Plan +'),
      monthlyPrice: '$19.99',
      annualPrice: '$149.99', // $239.88 annually, discounted to $149.99
      annualOriginalPrice: '$239.99',
      icon: '🌳',
      featureIcon: '🚀',
      features: [
        t('pricing.premium.features.additional_households', "1 Additional Shared Households"), 
        t('pricing.premium.features.up_to_8_members', "Up to 8 Members / House"), 
        t('pricing.premium.features.intelligent_calendar', "Intelligent Calendar"), 
        t('pricing.premium.features.ai_generated_tasks_asterisk', "AI-Generated Tasks*"), 
        t('pricing.premium.features.asset_vault_premium', "Asset Vault"), 
        t('pricing.premium.features.unlimited_data_history_premium', "Unlimited Data History"), 
        t('pricing.premium.features.commute_habit_iq_asterisk', "Commute & Habit IQ (Automations)*"),
        t('pricing.premium.features.increased_vote_power', "Increased Vote & Suggestion Power"),
        t('pricing.premium.features.priority_support', "Priority Support"),
        t('pricing.premium.features.early_access_features', "Early Access to New Features")
      ],
      notIncluded: [],
      buttonText: t('nav.get_started', 'Get Started'),
      accent: 'border-amber-500 shadow-xl shadow-amber-500/10'
    }
  ];

  const addOns = [
    { icon: Bot, name: t('pricing.addon_automations_name', 'Smart Automations'), desc: t('pricing.addon_automations_desc', 'Create tasks based on weather, mileage, and activity.') },
    { icon: Brain, name: t('pricing.addon_ai_name', 'AI Task Assistant'), desc: t('pricing.addon_ai_desc', 'Generate tailored schedules based on household habits.') },
    { icon: Car, name: t('pricing.addon_vehicle_name', 'Vehicle Intelligence'), desc: t('pricing.addon_vehicle_desc', 'Proactive reminders tied to real usage and predicted needs.') },
    { icon: Wrench, name: t('pricing.addon_maintenance_name', 'Advanced Maintenance'), desc: t('pricing.addon_maintenance_desc', 'Unlock detailed history and compliance tracking.') },
    { icon: BarChart3, name: t('pricing.addon_insights_name', 'Insights & Analytics'), desc: t('pricing.addon_insights_desc', 'Visualize activity trends and maintenance health.') },
    { icon: FileText, name: t('pricing.addon_files_name', 'File Storage'), desc: t('pricing.addon_files_desc', 'Attach receipts, photos, and manuals to any asset.') },
    { icon: Package, name: t('pricing.addon_backups_name', 'Data Backups'), desc: t('pricing.addon_backups_desc', 'Export household data anytime for peace of mind.') },
    { icon: Users, name: t('pricing.addon_capacity_name', 'Expanded Capacity'), desc: t('pricing.addon_capacity_desc', 'Add more members and households as life expands.') },
    { icon: Cat, name: t('pricing.addon_pets_name', 'Pet Care & Tracking'), desc: t('pricing.addon_pets_desc', 'Vaccinations, medications, and care reminders.') },
    { icon: Sparkles, name: t('pricing.addon_eva_name', 'Eva Assistant'), desc: t('pricing.addon_eva_desc', 'Natural language task and event management.') },
    { icon: ShoppingBasket, name: t('pricing.addon_groceries_name', 'Advanced Groceries'), desc: t('pricing.addon_groceries_desc', 'Smart categorization and consumption tracking.') },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white animate-in fade-in duration-500 transition-colors">
      
      {/* 1. Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto sticky top-0 bg-slate-50/80 dark:bg-slate-950/80 backdrop-blur-md z-50">
        <div onClick={() => navigate('/')} className="flex items-center gap-2 font-black text-2xl tracking-tighter cursor-pointer group">
          <div className="p-1.5 rounded-xl bg-brand text-white shadow-lg group-hover:scale-110 transition-transform">
            <Home className="w-6 h-6" />
          </div>
          <span>{t('general.everyday_os', 'Everyday')}<span className="text-brand">OS</span></span>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-1 bg-white dark:bg-slate-900 p-1 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
            {['en', 'fr', 'es'].map((lng) => (
              <button 
                key={lng}
                onClick={() => i18n.changeLanguage(lng)} 
                className={`px-3 py-1.5 text-2xs font-black rounded-lg transition-all ${
                  i18n.language.startsWith(lng) 
                    ? 'bg-brand text-white shadow-md' 
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>
          <Button onClick={() => navigate('/login')} variant="ghost" className="font-bold rounded-xl">
            {t('nav.login', 'Login')}
          </Button>
        </div>
      </nav>

      {/* 2. Header */}
      <header className="px-8 pt-16 pb-12 text-center max-w-4xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight whitespace-pre-line">
          {t('pricing.one_system', 'One system.')} <br /><span className="text-brand">{t('pricing.tailored', 'Tailored to you.')}</span>
        </h1>
        <p className="text-xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
          {t('pricing.subtitle', 'Choose a foundation that fits your size, then add the power-ups that matter most to your shared life.')}
        </p>
      </header>

      {/* Toggle for Monthly/Annual */} 
      <div className="flex justify-center mb-12">
        <div className="p-1 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center shadow-inner">
          <button 
            onClick={() => setIsAnnual(false)} 
            className={`px-6 py-2 rounded-full font-bold text-sm transition-all ${!isAnnual ? 'bg-brand text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-brand dark:hover:text-brand'}`}
          >
            {t('pricing.monthly', 'Monthly')}
          </button>
          <button 
            onClick={() => setIsAnnual(true)} 
            className={`px-6 py-2 rounded-full font-bold text-sm transition-all ${isAnnual ? 'bg-brand text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-brand dark:hover:text-brand'}`}
          >
            {t('pricing.annually_save', 'Annually')} <span className="ml-2 text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full dark:bg-blue-700 dark:text-blue-100">{t('pricing.save_25_plus', 'SAVE 25% +')}</span>
          </button>
        </div>
      </div>

      {/* 3. Core Plans Grid */}
      <main className="px-8 py-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {corePlans.map((plan) => (
            <div key={plan.id} className={`bg-white dark:bg-slate-900 p-8 rounded-[3rem] border-2 flex flex-col relative transition-all hover:shadow-2xl hover:scale-[1.01] ${plan.accent}`}>
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand text-white px-4 py-1 rounded-full text-2xs font-black uppercase tracking-widest shadow-lg">
                  {t('pricing.popular', 'Most Popular')}
                </div>
              )}
              
              <div className="mb-8">
                <span className="text-4xl mb-4 block">{plan.icon}</span>
                <h3 className="text-2xl font-black mb-1">{plan.name}</h3>
                <p className="text-sm font-bold text-slate-400 mb-6">{plan.tagline}</p>
                <div className="flex items-baseline gap-1">
                  {isAnnual && plan.annualOriginalPrice && (
                    <span className="text-xl font-bold text-slate-400 line-through mr-2">{plan.annualOriginalPrice}</span>
                  )}
                  <span className="text-5xl font-black">
                    {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-slate-400 font-bold">
                    {isAnnual && plan.id !== 'free' ? '/yr' : '/mo'}
                  </span>
                </div>
              </div>

              <div className="space-y-4 mb-10 flex-1">
                {plan.features.map((f, idx) => (
                  <div key={`${plan.id}-feat-${idx}`} className="flex items-start gap-3 text-sm font-bold text-slate-700 dark:text-slate-300">
                    <span className="shrink-0 text-base">{plan.featureIcon}</span>
                    <span className="leading-tight">{f}</span>
                  </div>
                ))}
                
                {plan.notIncluded.map((f, idx) => (
                  <div key={`${plan.id}-not-${idx}`} className="flex items-start gap-3 text-sm font-bold text-slate-300 dark:text-slate-600 opacity-50">
                    <X className="w-5 h-5 shrink-0" />
                    <span className="leading-tight">{f}</span>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => navigate('/login')} 
                className={`w-full rounded-2xl h-14 font-black text-lg shadow-lg ${
                  plan.recommended 
                    ? 'bg-brand text-white shadow-blue-600/20' 
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200'
                }`}
              >
                {plan.buttonText}
              </Button>
            </div>
          ))}
        </div>
      </main>

      {/* Founders Badge Banner */}
      <div className="px-8 pb-12 max-w-7xl mx-auto">
        <div className="bg-slate-700 text-white p-8 rounded-[2rem] text-center shadow-lg flex flex-col md:flex-row items-center justify-center gap-4">
          <Sparkles className="w-8 h-8 shrink-0" />
          <p className="text-lg font-bold">
            {t('pricing.founders_badge_banner', 'Join the first 1000 subscribers and receive an exclusive <span className="underline">Founders Badge</span> and additional perks!')}
          </p>
        </div>
      </div>

      {/* 4. Add-ons Section */}
      <div className="relative">
        <section className="px-8 py-24 max-w-7xl mx-auto blur-sm grayscale pointer-events-none">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <h2 className="text-4xl font-black mb-4">{t('pricing.addons_title', 'A La Carte Add-ons')}</h2>
              <p className="text-lg text-slate-500 font-medium">
                {t('pricing.addons_desc', 'Customize your OS by adding specific module intelligence to any plan.')}
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/30">
              <Zap className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest whitespace-nowrap">
                {t('pricing.addons_one_click', 'One-click activation')}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {addOns.map((add, i) => (
              <div key={i} className="group p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 hover:border-brand transition-all hover:shadow-xl cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400 group-hover:bg-brand group-hover:text-white transition-colors">
                    <add.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-black text-slate-900 dark:text-white">{add.name}</h4>
                      <Plus className="w-4 h-4 text-brand opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 leading-relaxed">
                      {add.desc}
                    </p>
                  </div>
                </div>
              </div>
          ))}
          </div>
        </section>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-8xl font-black text-slate-400 dark:text-slate-600 opacity-70 rotate-[-20deg]">
            {t('pricing.coming_soon', 'Coming Soon')}
          </span>
        </div>
      </div>

      {/* Fair Use Policy */}
      <div className="px-8 py-12 max-w-7xl mx-auto text-sm text-slate-500 dark:text-slate-400">
        <p className="font-bold mb-2">{t('pricing.fair_use_policy_title', '*Intelligent OS Fair Use Policy:')}</p>
        <p>
          {t('pricing.fair_use_policy_content', 'To ensure peak performance for all users, the Premium plan includes a generous monthly allocation of AI operations designed to support standard household management, family scheduling, and maintenance planning. Usage that falls significantly outside the scope of personal/household management (such as commercial data processing or automated bulk creation) may be subject to rate limiting. We believe in providing "Unlimited Intelligence" for your home, within the bounds of a single household’s needs.')}
        </p>
      </div>

      {/* 5. Comprehensive Footer */}
      <footer className="bg-slate-50 dark:bg-slate-900 pt-20 pb-10 px-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 font-black text-xl tracking-tighter mb-4">
                <Home className="w-5 h-5 text-brand" />
                <span>{t('general.everyday_os', 'Everyday')}<span className="text-brand">OS</span></span>
              </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium leading-relaxed">
                {t('footer.tagline', 'The ultimate operating system for modern households. Organize, collaborate, and thrive.')}
              </p>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">{t('landing.footer_product', 'Product')}</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('nav.home', 'Home')}</Link></li>
                <li><Link to="/pricing" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-brand transition-colors underline decoration-blue-600 decoration-2 underline-offset-4">{t('landing.footer_pricing', 'Pricing')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">{t('landing.footer_legal', 'Legal')}</h4>
              <ul className="space-y-4">
                <li><Link to="/privacy" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('footer.privacy', 'Privacy')}</Link></li>
                <li><Link to="/terms" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('footer.terms', 'Terms')}</Link></li>
                <li><Link to="/disclaimer" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-brand transition-colors">{t('footer.safety', 'Safety')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">{t('landing.footer_support', 'Support')}</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                  <Mail className="w-4 h-4 text-brand" /> nathanielaniballi@gmail.com
                </li>
                <li className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                  <Globe className="w-4 h-4 text-brand" /> London, UK
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-2xs font-black uppercase tracking-[0.2em] text-slate-400">
            <p>© 2026 EverydayOS • Systems Operational</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
